import { useEffect, useMemo, useState } from 'react';
import {
  Banner,
  Box,
  Button,
  Divider,
  Flex,
  Grid,
  InlineStack,
  Modal,
  ModalBody,
  ModalFooter,
  OptionCard,
  Paragraph,
  Search,
  Truncated,
  useModal,
} from '@customerio/pluma-components/react';
import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';
import { useUpdateCollection } from '@/services/db/collection/mutations';
import type { Collection } from '@/services/db/collection/entity';

const MAX_ATTRIBUTES = 5;
const MIN_ATTRIBUTES = 1;

export type CollectionAttributesSelectModalData = {
  collection: Collection;
  workspaceId: number;
};

type AttributeCardProps = {
  attribute: string;
  canSelect?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
  isPlaceholder?: boolean;
  onSelect?: (attribute: string) => void;
  onDeselect: (attribute: string) => void;
};

function uniqueAttributes(attributes: string[]) {
  return [...new Set(attributes)];
}

function AttributeCard({
  attribute,
  canSelect = true,
  isSelected = false,
  isDisabled = false,
  isPlaceholder = false,
  onSelect,
  onDeselect,
}: AttributeCardProps) {
  const handleCheckedChange = (checked: boolean) => {
    if (checked) {
      onSelect?.(attribute);
    } else {
      onDeselect(attribute);
    }
  };

  return (
    <OptionCard
      data-test-attribute-card={attribute}
      value={attribute}
      inputType="checkbox"
      size="sm"
      isSelected={isSelected}
      canSelect={canSelect}
      isDisabled={isDisabled || isPlaceholder}
      onCheckedChange={handleCheckedChange}
      ariaLabel={isPlaceholder ? 'Empty attribute slot' : attribute}
    >
      <Flex justifyContent="center" w="full" minW="0">
        <Truncated color={isSelected || canSelect ? undefined : 'disabled'} textTransform="none">
          {isPlaceholder ? '…' : attribute}
        </Truncated>
      </Flex>
    </OptionCard>
  );
}

// Pads the selection out to the cap with empty slots, so the grid always shows five cards.
function getVisibleSelectedAttributes(selectedAttributes: string[]): string[] {
  const attributes = [...selectedAttributes];

  for (let index = attributes.length; index < MAX_ATTRIBUTES; index += 1) {
    attributes.push('');
  }

  return attributes;
}

type AttributeCountTextProps = {
  selectedAttributesCount: number;
};

function AttributeCountText({ selectedAttributesCount }: AttributeCountTextProps) {
  return (
    <Paragraph variant="secondary" data-test-attributes-count-text>
      {`${selectedAttributesCount} of ${MAX_ATTRIBUTES} selected`}
    </Paragraph>
  );
}

type UseCollectionAttributesSelectModalStateProps = {
  collection: Collection;
  modalState: PlumaManagedModalProps<CollectionAttributesSelectModalData>['modalState'];
};

function removeFirstAttribute(attributes: string[], attributeToRemove: string): string[] {
  const attributeIndex = attributes.indexOf(attributeToRemove);

  if (attributeIndex === -1) {
    return attributes;
  }

  return attributes.filter((_, index) => index !== attributeIndex);
}

function useCollectionAttributesSelectModalState({
  collection,
  modalState,
}: UseCollectionAttributesSelectModalStateProps) {
  const availableAttributes = useMemo(() => uniqueAttributes(collection.schema), [collection.schema]);
  const initialSelected = useMemo(
    () =>
      collection.displayed_attributes.length > 0
        ? collection.displayed_attributes
        : availableAttributes.slice(0, MAX_ATTRIBUTES),
    [availableAttributes, collection.displayed_attributes],
  );
  const [selectedAttributes, setSelectedAttributes] = useState<string[]>(initialSelected);
  const [search, setSearch] = useState('');
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    if (modalState.isOpen !== true) return;

    setSelectedAttributes(initialSelected);
    setSearch('');
    setHasError(false);
  }, [initialSelected, modalState.isOpen]);

  const canAddColumn = selectedAttributes.length < MAX_ATTRIBUTES;
  const filteredAttributes = useMemo(() => {
    const normalizedSearch = search.toLowerCase().trim();
    if (normalizedSearch === '') return availableAttributes;
    return availableAttributes.filter((attribute) => attribute.toLowerCase().includes(normalizedSearch));
  }, [availableAttributes, search]);
  const visibleSelectedAttributes = useMemo(
    () => getVisibleSelectedAttributes(selectedAttributes),
    [selectedAttributes],
  );

  const addColumn = (column: string) => {
    if (selectedAttributes.includes(column) || !canAddColumn) return;
    setSelectedAttributes([...selectedAttributes, column]);
  };

  const removeColumn = (column: string) => {
    if (selectedAttributes.length === MIN_ATTRIBUTES) return;
    setSelectedAttributes(removeFirstAttribute(selectedAttributes, column));
  };

  return {
    addColumn,
    canAddColumn,
    filteredAttributes,
    hasError,
    removeColumn,
    search,
    selectedAttributes,
    setHasError,
    setSearch,
    visibleSelectedAttributes,
  };
}

export function CollectionAttributesSelectModal({
  modalState,
  data,
  onClose,
  onConfirm,
}: PlumaManagedModalProps<CollectionAttributesSelectModalData>) {
  const { collection, workspaceId } = data;
  const update = useUpdateCollection({ workspaceId });
  const modal = useCollectionAttributesSelectModalState({ collection, modalState });

  const handleSubmit = async () => {
    try {
      await update.mutateAsync({
        id: collection.id,
        update: { update_type: 'displayed_attributes', displayed_attributes: modal.selectedAttributes },
      });
      modal.setHasError(false);
      onConfirm();
      onClose();
    } catch {
      modal.setHasError(true);
    }
  };

  return (
    <Modal
      state={modalState}
      onClose={onClose}
      title="Edit displayed attributes"
      isDismissable={!update.isPending}
      shouldShowCloseButton={true}
      shouldScrollInViewport={true}
    >
      <ModalBody>
        <InlineStack alignBlock="center" alignInline="space-between">
          <Box style={{ width: '352px' }}>
            <Search
              data-test-attributes-search-input
              ariaLabel="Attribute name"
              placeholder="Find by attribute name…"
              value={modal.search}
              onSearchInput={modal.setSearch}
              onSearch={() => {}}
              shouldSearchOnInput={true}
            />
          </Box>

          <AttributeCountText selectedAttributesCount={modal.selectedAttributes.length} />
        </InlineStack>

        <Divider my="200" />

        <Paragraph variant="secondary" mb="100">
          Choose which columns to display:
        </Paragraph>

        <Grid data-test-selected-attributes gap="100" columns={4}>
          {modal.visibleSelectedAttributes.map((attribute, index) => (
            <AttributeCard
              key={`${attribute}-${index}`}
              attribute={attribute}
              isSelected={modal.selectedAttributes.includes(attribute)}
              isDisabled={attribute === '' || modal.selectedAttributes.length === MIN_ATTRIBUTES}
              isPlaceholder={attribute === ''}
              onDeselect={modal.removeColumn}
            />
          ))}
        </Grid>

        <Divider my="200" />

        <Grid
          data-test-visible-attributes
          gap="100"
          mb="200"
          columns={modal.filteredAttributes.length > 0 ? 4 : undefined}
        >
          {modal.filteredAttributes.length > 0 ? (
            modal.filteredAttributes.map((attribute) => (
              <AttributeCard
                key={attribute}
                attribute={attribute}
                canSelect={modal.canAddColumn}
                isSelected={modal.selectedAttributes.includes(attribute)}
                isDisabled={
                  modal.selectedAttributes.length === MIN_ATTRIBUTES && modal.selectedAttributes.includes(attribute)
                }
                onSelect={modal.addColumn}
                onDeselect={modal.removeColumn}
              />
            ))
          ) : (
            <Paragraph variant="secondary" my="075">
              {modal.search !== '' ? `No attributes match ${modal.search}.` : 'No attributes available.'}
            </Paragraph>
          )}
        </Grid>

        {modal.hasError ? (
          <Banner variant="error" mt="200" data-test-save-error>
            Save failed. Try again.
          </Banner>
        ) : null}
      </ModalBody>

      <ModalFooter>
        <Button data-test-cancel-button variant="subtle" onClick={onClose} isDisabled={update.isPending}>
          Cancel
        </Button>

        <Button
          data-test-save-button
          isDisabled={update.isPending}
          isLoading={update.isPending}
          variant="primary"
          onClick={() => void handleSubmit()}
        >
          Save attributes
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export function useCollectionAttributesSelectModal() {
  return useModal(CollectionAttributesSelectModal);
}
